import React, { useState } from "react";
// ArrowDownTrayIcon history : the original import was dead (never rendered)
// and was removed by the 2026-07-04 tools-wide overhaul, audit item
// Tier 1 #4, when .jsx linting surfaced it as no-unused-vars. The
// 2026-08-07 download/copy consistency pass (operator-directed)
// re-added it wired to a REAL Download button (handleDownloadRecords
// below), completing what the dead import only gestured at.
import {
  ShieldCheckIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import SEO from "../components/SEO";
import api from "../utils/api";
import { downloadFile } from "../utils/helpers";
import { generateStandardFilename } from "../../shared/utils/fileHelpers";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
// 2026-06-01 : real Top-Level Domain (TLD) validation via tldts,
// shared with the server. See src/shared/utils/domainValidation.js.
import { isRegistrablePublicDomain } from "../../shared/utils/domainValidation";

// 2026-07-04 tools-wide overhaul (operator-approved), audit items #10
// and #17 : the summary card previously rendered a hard-coded
// "Sectigo" label for EVERY entry in summary.certificateAuthorities
// (a dual-CA-era leftover that papered over unknown keys being echoed
// back). The service (src/server/services/caaService.js:CAAService) now only
// returns known CA keys, and this map turns each key into its
// customer-facing display name. The ® mark travels with the brand
// name in customer text (2026-07-04 tools-wide overhaul reviewer
// sweep, matching the Trustico® house treatment).
const CA_DISPLAY_NAMES = {
  sectigo: "Sectigo®",
};

function CAARecordGenerator() {
  const [domain, setDomain] = useState("");
  const [selectedCAs, setSelectedCAs] = useState({
    sectigo: true,
  });
  const [recordTypes, setRecordTypes] = useState({
    issue: true,
    issuewild: true,
    issuemail: true,
    iodef: false,
  });
  const [iodefEmail, setIodefEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item
  // #12 : state-driven transient "Copied!" feedback replacing the
  // previous SILENT copies. Now the shared success-gated hook (see
  // src/client/hooks/useCopyFeedback.js) : the status holds at most
  // one key ("all" or "record-<index>") and clears after 2 seconds.
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const resultsRef = useScrollToResults(result);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!domain.trim()) {
      setError("A domain or subdomain is required.");
      return;
    }
    // Real Top-Level Domain (TLD) check for instant feedback ; the
    // server applies the same gate authoritatively.
    if (!isRegistrablePublicDomain(domain.trim().replace(/^\*\./, ""))) {
      setError(
        "The domain name uses an unrecognized or non-public Top-Level Domain (TLD)."
      );
      return;
    }

    if (!selectedCAs.sectigo) {
      setError("Select at least one Certificate Authority (CA).");
      return;
    }

    if (recordTypes.iodef && !iodefEmail.trim()) {
      setError("An e-mail address is required for IODEF records.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    try {
      const response = await api.generateCAA({
        domain,
        certificateAuthorities: selectedCAs,
        recordTypes,
        iodefEmail: recordTypes.iodef ? iodefEmail : "",
      });
      // 2026-07-04 tools-wide overhaul (operator-approved), audit
      // item #9 : /api/caa/generate now returns the standard
      // { success: true, data: { domain, records, summary } }
      // envelope, so the payload lives under response.data (matching
      // how CAAChecker and CSRGenerator consume their endpoints).
      setResult(response.data);
    } catch (err) {
      setError(err.message || "The CAA records could not be generated.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDomain("");
    setSelectedCAs({ sectigo: true });
    setRecordTypes({
      issue: true,
      issuewild: true,
      issuemail: true,
      iodef: false,
    });
    setIodefEmail("");
    setResult(null);
    setError(null);
    resetCopyStatus();
  };

  /**
   * The one zone line behind the display, per-row copy, Copy
   * Everything, and
   * Download, so the affordances cannot drift apart. `record.raw` is
   * the SERVER's own composition (caaService.generateRecords emits it
   * byte-identical to `host. IN CAA value` on every record type) :
   * using it directly, rather than rebuilding the line client-side,
   * also closes the client-format-vs-server-format drift channel. No
   * client-side escaping is applied : the interpolated pieces are the
   * service's own CA_CONFIGS constants and the IODEF e-mail, which
   * caaService validates against the shared EMAIL_REGEX (a character
   * class permitting no quotes or backslashes) before composing.
   * Unlike the CAA Checker, whose values come from arbitrary live
   * DNS, this page never serializes untrusted text. The two CAA pages
   * are deliberately separate tools with their own serializers
   * (operator decision 2026-08-07) ; they emit the same zone-line
   * shape by design.
   */
  const serializeRecord = (record) => record.raw;

  // Per-record copy keyed by row index (audit item #12) : the shared
  // hook gates the "Copied!" confirmation on the clipboard helper's
  // success flag, so a failed copy never claims otherwise.
  const handleCopyRecord = (record, index) =>
    handleCopy(serializeRecord(record), `record-${index}`);

  const handleCopyAll = () => {
    if (!result || !result.records) return;
    handleCopy(result.records.map(serializeRecord).join("\n"), "all");
  };

  // 2026-08-07 download/copy consistency pass (operator-directed) :
  // every tool whose result is a savable artifact offers a Download
  // button under the standard {identifier}-{type}-{timestamp} filename
  // (docs/FILENAME_STANDARDS.md). The file content is byte-identical
  // to what Copy Everything puts on the clipboard (the zone-style
  // "host. IN CAA value" lines), so a customer gets the same records
  // whichever affordance they use.
  const handleDownloadRecords = () => {
    // Length check, not just presence : an all-record-types-unchecked
    // submit legitimately returns records: [] (caaService.generateRecords
    // appends per selected type), and a bare presence guard would write
    // a zero-byte file. The header buttons are gated on the same
    // condition, mirroring the CAAChecker hasRecords gate.
    if (!result?.records?.length) return;
    const allRecords = result.records.map(serializeRecord).join("\n");
    downloadFile(
      allRecords,
      generateStandardFilename(result.domain, "caa-records", "txt")
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="CAA Record Generator - SSL Certificate Authority Authorization"
        description="Generate CAA (Certificate Authority Authorization) records for Sectigo® SSL Certificates. Control which Certificate Authorities can issue SSL Certificates for your domain."
        keywords="CAA record, CAA record generator, Certificate Authority Authorization, Sectigo® CAA, SSL Certificate CAA, DNS CAA record"
        canonicalUrl="https://tools.trustico.com/caa"
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          CAA Record Generator
        </h1>
        <p className="text-body">
          Generate CAA (Certificate Authority Authorization) DNS records that
          control which Certificate Authorities are allowed to issue SSL
          Certificates for your domain names. Publishing CAA records is a simple
          safeguard, because Certificate Authorities are required to check them
          before issuing. Choose the record types you need, covering standard
          and Wildcard SSL Certificate issuance, S/MIME issuance and violation
          reporting, and copy the finished records into your Domain Name System
          (DNS) zone.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Domain or Subdomain
              </h2>
              <p className="text-body mb-3">
                Enter the domain or subdomain for which you want to generate CAA
                records. CAA records can be set at any level of your domain
                hierarchy. For example, you can set CAA records for example.com
                (root domain), www.example.com (subdomain), or mail.example.com
                (subdomain).
              </p>
              <p className="text-body mb-4">
                Do not include http:// or https:// protocols. Enter only the
                domain name such as example.com or subdomain.example.com.
              </p>
              {/* 2026-07-04 operator consistency directive : the HTML
                  `required` attribute was dropped because the disabled
                  gate on the submit button already blocks empty
                  click-submits and the handleSubmit trim guard covers
                  direct form submission ; the browser-native bubble the
                  attribute produced did not match the standard
                  error Alert treatment (the reference page,
                  CSRGenerator.jsx, carries no `required` attributes). */}
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="input"
              />
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Certificate Authorities
              </h2>
              <p className="text-body mb-3">
                Select which Certificate Authorities are allowed to issue SSL
                Certificates for your domain.
              </p>
              <p className="text-body mb-4">
                Sectigo® is used to ensure compatibility with all SSL
                Certificates we provide.
              </p>
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCAs.sectigo}
                    onChange={(e) =>
                      setSelectedCAs((prev) => ({
                        ...prev,
                        sectigo: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm"
                  />
                  <span className="ml-3 text-heading">
                    <span className="font-medium">Sectigo® CAA Records</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Record Types
              </h2>
              <p className="text-body mb-4">
                Choose which types of SSL Certificates the selected Certificate
                Authorities can issue. All types are enabled by default for
                maximum compatibility.
              </p>
              <div className="space-y-6">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recordTypes.issue}
                    onChange={(e) =>
                      setRecordTypes((prev) => ({
                        ...prev,
                        issue: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-heading">Issue</span>
                    <p className="text-body mt-2">
                      The &apos;issue&apos; record type allows the specified
                      Certificate Authorities to issue standard SSL Certificates
                      for your domain. This includes single-domain SSL
                      Certificates, multi-domain (SAN) SSL Certificates, and
                      subdomain-specific SSL Certificates.
                    </p>
                    <p className="text-body mt-3">
                      This is the most common CAA record type used for standard
                      website SSL Certificate issuance.
                    </p>
                  </div>
                </label>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recordTypes.issuewild}
                    onChange={(e) =>
                      setRecordTypes((prev) => ({
                        ...prev,
                        issuewild: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-heading">
                      Issue Wildcard
                    </span>
                    <p className="text-body mt-2">
                      The &apos;issuewild&apos; record type specifically
                      controls the issuance of Wildcard SSL Certificates
                      (*.domain.com) that can secure unlimited subdomains.
                    </p>
                    <p className="text-body mt-3">
                      If you want to allow Wildcard SSL Certificate issuance,
                      this record must be enabled. Note that having only
                      &apos;issue&apos; enabled will not permit Wildcard SSL
                      Certificates.
                    </p>
                  </div>
                </label>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recordTypes.issuemail}
                    onChange={(e) =>
                      setRecordTypes((prev) => ({
                        ...prev,
                        issuemail: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-heading">Issue Mail</span>
                    <p className="text-body mt-2">
                      The &apos;issuemail&apos; record type authorizes the
                      issuance of S/MIME SSL Certificates for secure e-mail
                      communications.
                    </p>
                    <p className="text-body mt-3">
                      S/MIME SSL Certificates enable e-mail encryption and
                      digital signatures, ensuring that your e-mail
                      communications remain private and authenticated.
                    </p>
                  </div>
                </label>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recordTypes.iodef}
                    onChange={(e) =>
                      setRecordTypes((prev) => ({
                        ...prev,
                        iodef: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-link focus:ring-blue-500 border-control rounded-sm mt-1"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-heading">IODEF</span>
                    <p className="text-body mt-2">
                      The &apos;iodef&apos; record type enables incident
                      reporting for SSL Certificate policy violations. When a
                      Certificate Authority receives a request that violates
                      your CAA policy, they will send a notification to your
                      specified e-mail address.
                    </p>
                    <p className="text-body mt-3">
                      This provides an additional security layer by alerting you
                      to unauthorized SSL Certificate issuance attempts.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {recordTypes.iodef && (
              <div className="card p-6">
                <h2 className="text-lg font-semibold text-heading mb-2">
                  IODEF E-Mail Address
                </h2>
                <p className="text-body mb-4">
                  Provide an e-mail address where incident reports about SSL
                  Certificate policy violations will be sent. This helps you
                  monitor unauthorized SSL Certificate issuance attempts.
                </p>
                {/* `required` dropped for the same reason as the domain
                    input above (2026-07-04 operator consistency
                    directive) : the disabled gate plus the iodef branch
                    of the handleSubmit guard cover both submit paths. */}
                <input
                  type="email"
                  value={iodefEmail}
                  onChange={(e) => setIodefEmail(e.target.value)}
                  placeholder="security@example.com"
                  className="input"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            {/* 2026-07-04 operator consistency directive : the primary
                action is disabled until every required text input is
                non-empty (domain always ; the IODEF e-mail only while
                the IODEF record type is enabled), matching the
                ChainAnalyzer / CertificateMatcher gate pattern. The
                Certificate Authority checkbox is deliberately NOT part
                of the gate : it is a choice rather than a text input,
                and the handleSubmit guard explains the requirement in
                words ("Select at least one Certificate Authority (CA).")
                when it is missed. handleSubmit also keeps its trim
                guards as the direct-form-submission backstop. */}
            <ActionButton
              type="submit"
              variant="primary"
              disabled={
                loading ||
                !domain.trim() ||
                (recordTypes.iodef && !iodefEmail.trim())
              }
              loading={loading}
              loadingText="Generating..."
              icon={ShieldCheckIcon}
            >
              Generate CAA Records
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && result.records && (
        <div ref={resultsRef} className="mt-8 animate-fade-in">
          <div className="card p-6">
            <div className="flex justify-between items-center mb-2">
              {/* 2026-07-04 operator consistency directive : recased
                  from h2 text-2xl font-bold to the reference section
                  heading size (text-lg font-semibold) - the h1 page
                  title is the only larger heading a tool page carries. */}
              <h2 className="text-lg font-semibold text-heading">
                Generated CAA Records
              </h2>
              {/* 2026-08-07 download/copy consistency pass
                  (operator-directed) : Copy Everything gains a Download
                  sibling, grouped in the flex gap-3 header pair the
                  PasswordGenerator results header established. The
                  pair is gated on a non-empty record list (the
                  CAAChecker hasRecords idiom) : an
                  all-record-types-unchecked submit returns zero
                  records, and there is nothing to copy or save. */}
              {result.records.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  <ActionButton
                    variant="outline"
                    onClick={handleCopyAll}
                    icon={ClipboardDocumentIcon}
                  >
                    {/* Transient label swap driven by copyStatus (audit
                      item #12) - same idiom as the "Copy CSR" button in
                      CSRGenerator.jsx. Label normalized from "Copy All
                      Records" to the one the sibling copy-everything
                      buttons on CAAChecker and PasswordGenerator use
                      (2026-07-04 operator consistency directive) - and
                      all three moved together from "Copy All" to "Copy
                      Everything" on 2026-08-23, "All" being a
                      capitalised three-letter word his heading rule
                      says to reword away rather than keep. */}
                    {copyStatus.all ? "Copied!" : "Copy Everything"}
                  </ActionButton>
                  <ActionButton
                    variant="outline"
                    onClick={handleDownloadRecords}
                    icon={ArrowDownTrayIcon}
                  >
                    Download
                  </ActionButton>
                </div>
              )}
            </div>

            <p className="text-body mb-4">
              Add these CAA records to your domain&apos;s DNS settings through
              your DNS provider or domain registrar. Once added, only the
              specified Certificate Authorities will be able to issue SSL
              Certificates for your domain. DNS propagation typically takes
              15-30 minutes.
            </p>

            <div className="space-y-3">
              {result.records.map((record, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">
                        {record.description}
                      </p>
                      {/* Rendered through the same serializeRecord
                          the copy and download payloads use, so what
                          the customer reads is byte-identical to what
                          they copy or save. */}
                      <code className="font-mono text-heading">
                        {serializeRecord(record)}
                      </code>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyRecord(record, index)}
                      className="ml-4 p-2 text-muted hover:text-body transition-colors"
                      title="Copy to Clipboard"
                      aria-label={
                        /* Indexed so screen readers can tell the
                           rows' otherwise identically-named copy
                           buttons apart ; the visual tooltip (title)
                           keeps the generic wording. */
                        copyStatus[`record-${index}`]
                          ? "Copied!"
                          : `Copy Record ${index + 1}`
                      }
                    >
                      {/* Icon-only button, so the "Copied!" feedback
                          replaces the icon for 2 seconds (audit item
                          #12 - the state-driven pattern from
                          CSRGenerator.jsx adapted to an icon
                          control). */}
                      {copyStatus[`record-${index}`] ? (
                        <span className="font-medium text-green-700 dark:text-green-400">
                          Copied!
                        </span>
                      ) : (
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {result.summary && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-heading">
                      {result.summary.totalRecords}
                    </p>
                    <p className="text-muted">Total Records</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-heading">
                      {/* Keys are already filtered to known CAs by
                          the service - see the CA_DISPLAY_NAMES
                          comment at the top of this file. The || ca
                          fallback can only fire if the service and
                          this map ever drift. */}
                      {result.summary.certificateAuthorities
                        .map((ca) => CA_DISPLAY_NAMES[ca] || ca)
                        .join(" + ")}
                    </p>
                    <p className="text-muted">Certificate Authorities</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-heading">
                      {result.summary.recordTypes.length}
                    </p>
                    <p className="text-muted">Record Types</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Implementation Guide
            </h2>
            <div className="space-y-3 text-body">
              <p>
                To implement these CAA records, start by copying all the
                generated records shown above. Then log in to your DNS provider
                or domain registrar&apos;s control panel. Each provider has a
                slightly different interface, but the process is generally
                similar across all platforms.
              </p>
              <p>
                Navigate to the DNS management section for your domain and look
                for the option to add new DNS records. Select CAA as the record
                type. If your provider doesn&apos;t show CAA in the dropdown
                menu, they may not support CAA records, and you should contact
                their support team for assistance.
              </p>
              <p>
                For each record, the host field should match your domain name
                (or subdomain if applicable), and the value field should contain
                the complete CAA record value exactly as provided, including the
                flags, tag, and Certificate Authority domain. Ensure you
                maintain the exact formatting and spacing shown in the generated
                records.
              </p>
              <p>
                After adding all records, save your changes. DNS propagation
                typically takes 15-30 minutes for most providers, though you
                should allow up to 24 hours for global propagation to complete.
                You can verify your CAA records are active using online CAA
                lookup tools or DNS query commands.
              </p>
              <p>
                Once active, these CAA records will ensure that only your
                selected Certificate Authorities can issue SSL Certificates for
                your domain. This provides an additional layer of security
                against unauthorized SSL Certificate issuance and helps protect
                your domain from potential SSL Certificate-based attacks.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CAARecordGenerator;
